'use client'

import { useState } from 'react'
import Link from 'next/link'
import { List, X } from '@phosphor-icons/react'
import { StoryhealthLogo } from './StoryhealthLogo'

interface StoryhealthNavItem {
  label: string
  href: string
  external?: boolean
}

interface StoryhealthNavBarProps {
  items?: StoryhealthNavItem[]
  logoHref?: string
  ctaLabel?: string
  ctaHref?: string
}

// Mirrors the real storyhealth.com nav (For Health Systems / For Providers /
// For Patients / CMS Access / Innovaccer), plus Resources, which points at
// this CMS's own Story Health stories/content — the real site has none
// today. "Resources" is the single content entry point (no separate
// "Stories" item) and links to the blogs/stories listing.
const DEFAULT_ITEMS: StoryhealthNavItem[] = [
  { label: 'FOR HEALTH SYSTEMS', href: 'https://www.storyhealth.com/health-systems', external: true },
  { label: 'FOR PROVIDERS',      href: 'https://innovaccer.com/cms-access-providers', external: true },
  { label: 'FOR PATIENTS',       href: 'https://www.storyhealth.com/cms-access-patients', external: true },
  { label: 'RESOURCES',          href: '/storyhealth/resources', external: false },
  { label: 'INNOVACCER',         href: 'https://innovaccer.com', external: true },
]

function NavLink({ item, onClick }: { item: StoryhealthNavItem; onClick?: () => void }) {
  if (item.external) {
    return <a href={item.href} className="sh-nav__item" onClick={onClick}>{item.label}</a>
  }
  return <Link href={item.href} className="sh-nav__item" onClick={onClick}>{item.label}</Link>
}

export function StoryhealthNavBar({
  items = DEFAULT_ITEMS,
  logoHref = 'https://www.storyhealth.com',
  ctaLabel = 'CONNECT WITH US',
  ctaHref = 'https://www.storyhealth.com/cms-access',
}: StoryhealthNavBarProps) {
  const [mobileOpen, setMobileOpen] = useState(false)

  return (
    <header className="sh-nav">
      <div className="sh-nav__card">
        <div className="sh-nav__left">
          <a href={logoHref} className="sh-nav__logo" aria-label="Story Health by Innovaccer">
            <StoryhealthLogo height={32} />
          </a>
          <nav className="sh-nav__menu" aria-label="Primary">
            {items.map((item) => <NavLink key={item.label} item={item} />)}
          </nav>
        </div>

        <div className="sh-nav__right">
          <a href={ctaHref} className="sh-nav__cta">
            <span className="sh-nav__cta-text">
              <span className="btn-primary-text">{ctaLabel}</span>
              <span className="btn-hover-text">{ctaLabel}</span>
            </span>
          </a>
          <button
            type="button"
            className="sh-nav__hamburger"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
          >
            {mobileOpen ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div className={`sh-nav__mobile${mobileOpen ? ' is-open' : ''}`}>
        <nav className="sh-nav__mobile-menu" aria-label="Mobile primary">
          {items.map((item) => (
            item.external
              ? <a key={item.label} href={item.href} className="sh-nav__mobile-nav-item" onClick={() => setMobileOpen(false)}>
                  <span className="sh-nav__mobile-nav-item__label">{item.label}</span>
                </a>
              : <Link key={item.label} href={item.href} className="sh-nav__mobile-nav-item" onClick={() => setMobileOpen(false)}>
                  <span className="sh-nav__mobile-nav-item__label">{item.label}</span>
                </Link>
          ))}
        </nav>
        <div className="sh-nav__mobile-ctas">
          <a href={ctaHref} className="sh-nav__cta sh-nav__cta--block" onClick={() => setMobileOpen(false)}>
            <span className="sh-nav__cta-text">
              <span className="btn-primary-text">{ctaLabel}</span>
              <span className="btn-hover-text">{ctaLabel}</span>
            </span>
          </a>
        </div>
      </div>
    </header>
  )
}

export default StoryhealthNavBar
